import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import type { DndAccountData, DndDay, DndScheduleWindow } from "~/lib/matrix";
import { useUnderline } from "~/lib/underline";
import { ICON_STROKE } from "~/lib/icon";

const DAYS: Array<{ value: DndDay; label: string }> = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];

export function DndScheduleDialog({
  schedule,
  onSave,
  onClose,
}: {
  schedule: DndAccountData["schedule"];
  onSave: (schedule: DndAccountData["schedule"]) => void;
  onClose: () => void;
}) {
  const titleId = useId();
  const portalRoot = usePortalRoot();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [enabled, setEnabled] = useState(schedule.enabled);
  const [windows, setWindows] = useState<DndScheduleWindow[]>(schedule.windows);

  useEffect(() => {
    const node = dialogRef.current;
    node?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!portalRoot) return null;

  const invalid = windows.some((window) => window.start === window.end || window.days.length === 0);
  const content = (
    <div
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 80,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--chat-space-4)",
        background: "var(--chat-scrim-dialog)",
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{
          width: 560,
          maxWidth: "100%",
          borderRadius: "var(--chat-radius-xl)",
          background: "var(--chat-surface-dialog)",
          border: "1px solid var(--chat-surface-border)",
          boxShadow: "var(--chat-shadow-dialog)",
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "var(--chat-space-4)",
          }}
        >
          <div>
            <h2
              id={titleId}
              style={{
                margin: 0,
                fontSize: "var(--chat-type-display-sm-size)",
                lineHeight: "var(--chat-type-display-sm-line)",
                fontWeight: 500,
              }}
            >
              Weekly schedule
            </h2>
            <p
              style={{
                margin: 0,
                color: "var(--chat-fg-secondary)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
              }}
            >
              Local time on this device
            </p>
          </div>
          <button type="button" aria-label="Cancel" className="chat-button" onClick={onClose}>
            <X size={16} strokeWidth={ICON_STROKE} />
          </button>
        </div>
        <div style={{ padding: "0 var(--chat-space-4) var(--chat-space-4)" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "var(--chat-space-2)" }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => setEnabled(event.currentTarget.checked)}
              style={{ accentColor: "var(--chat-fg-primary)" }}
            />
            <span style={{ fontSize: "var(--chat-type-body-md-size)" }}>Enable schedule</span>
          </label>
          <div
            style={{
              display: "grid",
              gap: "var(--chat-space-2)",
              marginTop: "var(--chat-space-4)",
            }}
          >
            {windows.map((window, index) => (
              <ScheduleRow
                key={index}
                window={window}
                onChange={(next) =>
                  setWindows(windows.map((item, i) => (i === index ? next : item)))
                }
                onDelete={() => setWindows(windows.filter((_, i) => i !== index))}
              />
            ))}
          </div>
          {invalid ? (
            <p
              role="alert"
              style={{
                margin: "var(--chat-space-3) 0 0",
                color: "var(--chat-state-danger)",
                fontSize: "var(--chat-type-body-sm-size)",
              }}
            >
              End time must differ from start time
            </p>
          ) : null}
          <button
            type="button"
            className="chat-button"
            onClick={() =>
              setWindows([...windows, { days: [1, 2, 3, 4, 5], start: "18:00", end: "09:00" }])
            }
            style={{
              marginTop: "var(--chat-space-4)",
              border: "none",
              borderRadius: "var(--chat-radius-sm)",
              background: "transparent",
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              fontWeight: 500,
              padding: "var(--chat-space-1) var(--chat-space-2)",
              cursor: "pointer",
            }}
          >
            Add window
          </button>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "var(--chat-space-2)",
            padding: "var(--chat-space-4)",
          }}
        >
          <button
            type="button"
            className="chat-button"
            onClick={onClose}
            style={{
              height: "var(--chat-inline-edit-action-row-height)",
              padding: "0 var(--chat-space-3)",
              borderRadius: "var(--chat-radius-sm)",
              border: "none",
              background: "transparent",
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="chat-button"
            disabled={invalid}
            onClick={() => {
              onSave({ enabled, windows });
              onClose();
            }}
            style={{
              height: "var(--chat-inline-edit-action-row-height)",
              padding: "0 var(--chat-space-3)",
              borderRadius: "var(--chat-radius-md)",
              border: "none",
              background: "var(--chat-cta-bg)",
              color: "var(--chat-cta-fg)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              fontWeight: 500,
              cursor: invalid ? "not-allowed" : "pointer",
              opacity: invalid ? 0.5 : 1,
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
  return createPortal(content, portalRoot);
}

function ScheduleRow({
  window,
  onChange,
  onDelete,
}: {
  window: DndScheduleWindow;
  onChange: (window: DndScheduleWindow) => void;
  onDelete: () => void;
}) {
  const startUnderline = useUnderline();
  const endUnderline = useUnderline();
  const toggleDay = (day: DndDay) => {
    const days = window.days.includes(day)
      ? window.days.filter((value) => value !== day)
      : [...window.days, day].sort((a, b) => a - b);
    onChange({ ...window, days });
  };
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 88px 88px auto",
        gap: "var(--chat-space-2)",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--chat-space-1)" }}>
        {DAYS.map((day) => {
          const selected = window.days.includes(day.value);
          return (
            <button
              key={day.value}
              type="button"
              className="chat-button"
              aria-pressed={selected}
              onClick={() => toggleDay(day.value)}
              style={{
                minWidth: 36,
                minHeight: 28,
                border: "none",
                borderRadius: "var(--chat-radius-sm)",
                background: selected ? "var(--chat-surface-raised)" : "transparent",
                color: selected ? "var(--chat-fg-primary)" : "var(--chat-fg-secondary)",
                fontSize: "var(--chat-type-body-xs-size)",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {day.label}
            </button>
          );
        })}
      </div>
      <input
        aria-label="Start time"
        type="time"
        value={window.start}
        onChange={(event) => onChange({ ...window, start: event.currentTarget.value })}
        onFocus={startUnderline.onFocus}
        onBlur={startUnderline.onBlur}
        style={timeInputStyle(startUnderline.borderColor)}
      />
      <input
        aria-label="End time"
        type="time"
        value={window.end}
        onChange={(event) => onChange({ ...window, end: event.currentTarget.value })}
        onFocus={endUnderline.onFocus}
        onBlur={endUnderline.onBlur}
        style={timeInputStyle(endUnderline.borderColor)}
      />
      <button
        type="button"
        className="chat-button"
        onClick={onDelete}
        style={{
          border: "none",
          background: "transparent",
          color: "var(--chat-state-danger)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        Delete
      </button>
    </div>
  );
}

function timeInputStyle(borderColor: string): CSSProperties {
  return {
    minWidth: 0,
    border: "none",
    borderBottom: `1px solid ${borderColor}`,
    background: "transparent",
    color: "var(--chat-fg-primary)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-sm-size)",
    outline: "none",
    padding: "0 0 var(--chat-space-1)",
    transition: "border-color var(--chat-motion-fast) var(--chat-motion-ease)",
  };
}
